import * as React from 'react';

import classnames from 'classnames';

import {ActionMenu} from '../action-menu/action-menu';
import type {SelectMenuOption} from '../action-menu/action-menu';
import {colors} from '../colors/colors';
import {ChevronDownIcon, ChevronRightIcon, LoadingIcon} from '../icon';

import {Card} from './card';
import type {CardProps} from './card';

import styles from './collapsible-card.module.css';

type Props = {
    header: string | React.ReactNode;
    subheader?: string | React.ReactNode;
    padding?: 8 | 12 | 16 | 24;
    noBodyPadding?: boolean;
    isOpen: boolean;
    setIsOpen: (value: boolean) => void;
    isLoading?: boolean;
    headerActionMenuOptions?: SelectMenuOption[];
    getRef?: (node: HTMLDivElement | null) => void;
} & CardProps;

export function CollapsibleCard(props: Props) {
    const {
        header,
        subheader,
        padding = 16,
        noBodyPadding = false,
        children,
        isLoading,
        isOpen,
        setIsOpen,
        headerActionMenuOptions,
        getRef,
        ...cardProps
    } = props;

    const [isHovered, setIsHovered] = React.useState<boolean>(false);

    const horizontalPaddingStyle = classnames({
        'px-8': padding === 8,
        'px-12': padding === 12,
        'px-16': padding === 16,
        'px-24': padding === 24,
    });

    const topPaddingStyle = classnames({
        'pt-8': padding === 8,
        'pt-12': padding === 12,
        'pt-16': padding === 16,
        'pt-24': padding === 24,
    });

    const bottomPaddingStyle = classnames({
        'pb-8': padding === 8,
        'pb-12': padding === 12,
        'pb-16': padding === 16,
        'pb-24': padding === 24,
    });

    const bodyStyle = noBodyPadding
        ? undefined
        : classnames(horizontalPaddingStyle, bottomPaddingStyle);

    const iconStyle = classnames({
        [styles['icon-light']]: !isHovered,
        [styles['icon-dark']]: isHovered,
    });

    const headerStyle = classnames('flex', 'align-center', 'justify-sb', 'pointer-cursor', {
        'mb-8': isOpen,
        [bottomPaddingStyle]: !isOpen,
        [topPaddingStyle]: true,
        [horizontalPaddingStyle]: true,
    });

    const showHeaderActionMenu = Boolean(
        headerActionMenuOptions && headerActionMenuOptions.length > 0
    );

    return (
        <Card {...cardProps} fitHeight={true} getCardRef={getRef}>
            <div
                className={headerStyle}
                onClick={() => setIsOpen(!isOpen)}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <div className='flex flex-dir-col full-width'>
                    {header}
                    {subheader}
                </div>
                <div className='flex align-center gap-8 flex-shrink'>
                    {showHeaderActionMenu ? (
                        <div
                            onMouseEnter={() => setIsHovered(false)}
                            onMouseLeave={() => setIsHovered(true)}
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <ActionMenu size='small' options={headerActionMenuOptions ?? []} />
                        </div>
                    ) : null}
                    {isLoading && <LoadingIcon size={24} fill={colors.greyLt} />}
                    {!isLoading &&
                        (isOpen ? (
                            <ChevronDownIcon size={16} className={iconStyle} />
                        ) : (
                            <ChevronRightIcon size={16} className={iconStyle} />
                        ))}
                </div>
            </div>
            {isOpen && <div className={bodyStyle}> {children}</div>}
        </Card>
    );
}
